import { computed, inject, Injectable, resource, signal } from '@angular/core';
import {
  createInfiniteScroll,
  keepLastResolvedValue,
  type IInfiniteScrollCollectionVm,
  type IInfiniteScrollResult,
  type InfiniteScrollRefreshSource,
} from '@okcargo-apps/layout-ionic';
import { IFreightAvailable } from '@okcargo-apps/shared-domain';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import {
  ConnectivityService,
  FieldNames,
  FilterCombinator,
  FilterComparator,
  FilterGroupPayload,
  FindOptions,
  FreightAvailableQueryService,
  OrderType,
  SearchOptionsInput,
  normalizeFilterCombinators,
  type FilterOptions,
} from '@okcargo-apps/shared-infrastructure';
import { AccountService } from '../account';
import {
  FREIGHT_AVAILABLE_STATUS,
  IFreightAvailableAdditionalQuery,
  IFreightAvailableDetailedVM,
  IFreightAvailableFilters,
  IFreightAvailableQuery,
} from './freight-available-store.model';

@Injectable({
  providedIn: 'root',
})
export class FreightAvailableStoreService {
  private readonly freightAvailableQueryService = inject(FreightAvailableQueryService);
  private readonly accountService = inject(AccountService);
  private readonly connectivity = inject(ConnectivityService);

  private readonly PAGE_SIZE = 20;

  private readonly _activeFilters = signal<IFreightAvailableFilters | null>(null);

  private readonly _selectedFreightId = signal<string | null>(null);

  // Lightweight resource for the global counter of CREATED available freights.
  // It is refreshed on every polling tick and on every user-initiated refresh,
  // independently of the list the user is browsing.
  private readonly _freightAvailableTotalsResource = resource({
    params: () => (this.canViewMarket() ? true : null),
    loader: async ({ params, abortSignal }) => {
      if (!params) return 0;

      const options: SearchOptionsInput = {
        pageIndex: 0,
        pageSize: 1,
        filters: this.buildBaseFilterCriteria(),
        order: [{ field: FieldNames.LATEST_STARTING_AT, type: OrderType.ASC }],
        abortSignal,
      };

      const response = await this.freightAvailableQueryService.searchFreightsAvailable(options);
      return response.totalElements;
    },
  });

  // CREATED freights paginated list. It serves both the unfiltered default view
  // and the filtered view (the UI filters are part of the query identity, so any
  // filter change resets the pagination through the collection's linkedSignal).
  private readonly freightAvailableCollection = createInfiniteScroll<IFreightAvailableQuery, IFreightAvailable>({
    query: computed(() => (this.canViewMarket() ? { uiFilters: this._activeFilters() } : null)),
    pageSize: this.PAGE_SIZE,
    loadPage: (query, pageIndex, pageSize, abortSignal) =>
      this.searchFreights(this.buildFreightAvailableFilterCriteria(query.uiFilters), pageIndex, pageSize, abortSignal),
    getItemKey: (freight) => freight.id,
  });

  // Non-CREATED freights matching the user filters. By business decision they are
  // shown only once every CREATED freight matching those filters has been loaded.
  // The query includes that exhaustion gate: while it does not hold, params are null
  // and no query is fired; if it stops holding, resource aborts any in-flight request.
  private readonly freightAvailableAdditionalCollection = createInfiniteScroll<
    IFreightAvailableAdditionalQuery,
    IFreightAvailable
  >({
    query: computed(() => {
      if (!this.canViewMarket()) return null;
      const uiFilters = this._activeFilters();
      if (!uiFilters || !this.freightAvailableFilteredExhausted()) return null;
      return { uiFilters };
    }),
    pageSize: this.PAGE_SIZE,
    keepDataOnIdle: true,
    loadPage: (query, pageIndex, pageSize, abortSignal) =>
      this.searchFreights(
        this.buildFreightAvailableAdditionalFilterCriteria(query.uiFilters),
        pageIndex,
        pageSize,
        abortSignal,
      ),
    getItemKey: (freight) => freight.id,
  });

  private readonly _freightAvailableDetailed = resource({
    params: () => this._selectedFreightId(),
    loader: async ({ params: id, abortSignal }) => {
      if (!id) return null;

      const options: FindOptions = {
        id,
        abortSignal,
      };

      const response = await this.freightAvailableQueryService.findFreightAvailable(options);
      return response;
    },
  });

  // derived state (computed) & exposed state (public readonly)

  public readonly freightAvailableList = this.freightAvailableCollection.vm;

  public readonly freightAvailableAdditionalList = this.freightAvailableAdditionalCollection.vm;

  /*
   * Keeps the last loaded values when a reload fails (e.g. the connection drops
   * in the middle of a refresh) instead of blanking the view. The selected id is
   * used as the request key, so data from a previous freight is never shown as
   * if it were the selected one.
   */
  private readonly _lastResolvedFreightAvailable = keepLastResolvedValue(
    this._freightAvailableDetailed.snapshot,
    this._selectedFreightId,
  );

  // No key on purpose: the request of this resource does not depend on the user filters (its params
  // are just a flag and its criteria are the fixed base criteria), so the value is always valid.
  private readonly _lastResolvedTotals = keepLastResolvedValue(this._freightAvailableTotalsResource.snapshot);

  public readonly freightsAvailableTotalElements = computed(() => this._lastResolvedTotals() ?? 0);

  public readonly freightsAvailableFilteredTotalElements = computed(
    () => this.freightAvailableCollection.vm().totalElements,
  );

  public readonly freightAvailableFilteredExhausted = computed(() => {
    const vm: IInfiniteScrollCollectionVm<IFreightAvailable> = this.freightAvailableCollection.vm();
    return vm.lastLoadedAt !== null && !vm.isLoading && !vm.isLoadingMore && !vm.hasMore;
  });

  public readonly hasActiveFilters = computed(() => this._activeFilters() !== null);

  public readonly activeFilters = this._activeFilters.asReadonly();

  public readonly freightAvailableDetailed = computed(() => {
    const error = this._freightAvailableDetailed.error();

    const vm: IFreightAvailableDetailedVM = {
      item: this._lastResolvedFreightAvailable() ?? null,
      isLoading: this._freightAvailableDetailed.isLoading(),
      error: error,
      actionsDisabled: this.connectivity.isOffline(),
    };

    return vm;
  });

  // filters commands

  public applyFilters(filters: IFreightAvailableFilters): void {
    this._activeFilters.set(filters);
  }

  public clearFilters(): void {
    this._activeFilters.set(null);
  }

  public clearOriginFilter(): void {
    const current = this._activeFilters();
    if (!current) return;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { originCountryCode, originStateCode, ...rest } = current;
    this.updateOrClearRemainingFilters(rest);
  }

  public clearDestinationFilter(): void {
    const current = this._activeFilters();
    if (!current) return;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { destinationCountryCode, destinationStateCode, ...rest } = current;
    this.updateOrClearRemainingFilters(rest);
  }

  private updateOrClearRemainingFilters(remaining: Partial<IFreightAvailableFilters>): void {
    if (
      !remaining.originCountryCode &&
      !remaining.originStateCode &&
      !remaining.destinationCountryCode &&
      !remaining.destinationStateCode
    ) {
      this.clearFilters();
    } else {
      this._activeFilters.set(remaining as IFreightAvailableFilters);
    }
  }

  // QUERIES, imperative calls that force manual state updates

  public setSelectedFreightId(id: string | null): void {
    this._selectedFreightId.set(id);
  }

  public clearSelectedFreightId(): void {
    this._selectedFreightId.set(null);
  }

  public reloadFreightAvailableDetailed(): void {
    if (this.connectivity.isOffline()) return;
    this._freightAvailableDetailed.reload();
  }

  public reloadFreightAvailableList(source: InfiniteScrollRefreshSource = 'polling'): void {
    if (this.connectivity.isOffline()) return;
    this._freightAvailableTotalsResource.reload();
    this.freightAvailableCollection.refresh(source);
  }

  public loadFreightAvailablePage(): void {
    this.freightAvailableCollection.loadNextPage();
  }

  public retryFreightAvailable(): void {
    this.freightAvailableCollection.retry();
  }

  public loadNextFreightAvailableAdditionalPage(): void {
    this.freightAvailableAdditionalCollection.loadNextPage();
  }

  public retryFreightAvailableAdditional(): void {
    this.freightAvailableAdditionalCollection.retry();
  }

  // TODO review
  public reloadAfterSaveData(): void {
    setTimeout(() => {
      this.reloadFreightAvailableList();
      this.reloadFreightAvailableDetailed();
    }, 2000); // 2 sec
  }

  private canViewMarket(): boolean {
    return this.accountService.hasCapability(ECapabilities.VIEW_MARKET);
  }

  private searchFreights(
    filters: FilterOptions[],
    pageIndex: number,
    pageSize: number,
    abortSignal: AbortSignal,
  ): Promise<IInfiniteScrollResult<IFreightAvailable>> {
    const options: SearchOptionsInput = {
      pageIndex,
      pageSize,
      filters,
      order: [{ field: FieldNames.LATEST_STARTING_AT, type: OrderType.ASC }],
      abortSignal,
    };

    return this.freightAvailableQueryService.searchFreightsAvailable(options);
  }

  private buildBaseFilterCriteria(): FilterOptions[] {
    return [
      {
        combinator: FilterCombinator.NONE,
        field: FieldNames.STATUS,
        comparator: FilterComparator.IN,
        value: FREIGHT_AVAILABLE_STATUS,
        group: null as unknown as FilterGroupPayload,
      },
      {
        combinator: FilterCombinator.AND,
        field: FieldNames.LATEST_STARTING_AT,
        comparator: FilterComparator.GT,
        value: new Date().toISOString(),
        group: null as unknown as FilterGroupPayload,
      },
    ];
  }

  private buildFreightAvailableFilterCriteria(uiFilters: IFreightAvailableFilters | null): FilterOptions[] {
    return [...this.buildBaseFilterCriteria(), ...this.buildUiFilterCriteria(uiFilters)];
  }

  private buildFreightAvailableAdditionalFilterCriteria(uiFilters: IFreightAvailableFilters): FilterOptions[] {
    const criteria: FilterOptions[] = [
      ...this.buildUiFilterCriteria(uiFilters),
      {
        combinator: FilterCombinator.AND,
        field: FieldNames.STATUS,
        comparator: FilterComparator.NOT_IN,
        value: FREIGHT_AVAILABLE_STATUS,
        group: null as unknown as FilterGroupPayload,
      },
    ];

    return normalizeFilterCombinators(criteria);
  }

  private buildUiFilterCriteria(uiFilters: IFreightAvailableFilters | null): FilterOptions[] {
    if (!uiFilters) return [];

    const filterCriteria: FilterOptions[] = [];

    if (uiFilters.originCountryCode) {
      filterCriteria.push({
        combinator: FilterCombinator.AND,
        field: FieldNames.RAW_FIRST_STOP,
        comparator: FilterComparator.LIKE,
        value: '%"countryCode":"' + uiFilters.originCountryCode + '"%',
        group: null as unknown as FilterGroupPayload,
      });
    }
    if (uiFilters.originStateCode) {
      filterCriteria.push({
        combinator: FilterCombinator.AND,
        field: FieldNames.RAW_FIRST_STOP,
        comparator: FilterComparator.LIKE,
        value: '%"stateCode":"' + uiFilters.originStateCode + '"%',
        group: null as unknown as FilterGroupPayload,
      });
    }
    if (uiFilters.destinationCountryCode) {
      filterCriteria.push({
        combinator: FilterCombinator.AND,
        field: FieldNames.RAW_LAST_STOP,
        comparator: FilterComparator.LIKE,
        value: '%"countryCode":"' + uiFilters.destinationCountryCode + '"%',
        group: null as unknown as FilterGroupPayload,
      });
      if (uiFilters.destinationStateCode) {
        filterCriteria.push({
          combinator: FilterCombinator.AND,
          field: FieldNames.RAW_LAST_STOP,
          comparator: FilterComparator.LIKE,
          value: '%"stateCode":"' + uiFilters.destinationStateCode + '"%',
          group: null as unknown as FilterGroupPayload,
        });
      }
    }

    return filterCriteria;
  }
}
